import type { FileEntry, FilesClientMessage, FilesServerMessage } from '@one-deployment/protocol'
import { create } from 'zustand'
import { openBoxSocket, ReconnectingBoxSocket, type BoxSocketState } from './box'

export type { FileEntry }
export type FilesReady = Extract<FilesServerMessage, { type: 'ready' }>
export type FilesGit = Extract<FilesServerMessage, { type: 'git' }>

// Big frames and a deep send queue: each frame crosses the browser, the
// Worker relay and the box, so small ones left the link idle between them.
const CHUNK = 1024 * 1024
/** Pause sending while this much is still queued in the socket. */
const HIGH_WATER = 16 * 1024 * 1024

type Pending = { resolve: (v: Blob | number | void) => void; reject: (e: Error) => void; bytes?: number; parts?: ArrayBuffer[]; got?: number; onProgress?: (done: number) => void }

/**
 * The box's file panel socket (`/files/:session`, box-http files.rs). Folder
 * listings arrive whenever they change; uploads and downloads are one at a
 * time, so their binary frames are never interleaved.
 */
export class FilesClient {
  private sock: ReconnectingBoxSocket
  private pending = new Map<string, Pending>()
  private downloading: string | null = null
  private queue: Promise<unknown> = Promise.resolve()
  private watched = new Set<string>()

  constructor(sessionId: string, mode: 'ro' | 'rw', private readonly on: {
    ready: (r: FilesReady) => void
    listing: (path: string, entries: FileEntry[] | null) => void
    state: (s: BoxSocketState) => void
    git?: (g: FilesGit) => void
  }) {
    this.sock = new ReconnectingBoxSocket(() => openBoxSocket({ sessionId }, 'files', mode), {
      onOpen: () => { for (const p of this.watched) this.send({ type: 'list', path: p }) },
      onMessage: (d) => this.message(d),
      onState: (s) => {
        on.state(s)
        // A reconnect loses whatever was in flight; say so rather than hang.
        if (s !== 'open') this.failAll('The connection to the server dropped')
      },
    })
    this.sock.start()
  }

  stop(): void { this.failAll('Closed'); this.sock.stop() }

  /** Ask once for the session folder's git state; it arrives on `on.git`. */
  git(base?: string): void { this.send({ type: 'git', ...(base ? { base } : {}) }) }

  watch(path: string): void { this.watched.add(path); this.send({ type: 'list', path }) }
  forget(path: string): void { this.watched.delete(path); this.send({ type: 'forget', path }) }

  mkdir(path: string): Promise<void> { return this.request((id) => ({ type: 'mkdir', id, path })) as Promise<void> }
  remove(path: string): Promise<void> { return this.request((id) => ({ type: 'delete', id, path })) as Promise<void> }
  rename(from: string, to: string): Promise<void> { return this.request((id) => ({ type: 'rename', id, from, to })) as Promise<void> }

  upload(path: string, file: File, onProgress?: (done: number) => void): Promise<void> {
    return this.serial(async () => {
      const id = crypto.randomUUID()
      const done = this.track(id, { onProgress })
      this.send({ type: 'upload', id, path, bytes: file.size })
      const data = new Uint8Array(await file.arrayBuffer())
      const ws = this.sock.socket
      // Progress is what has left the browser, not what was queued.
      const sent = () => Math.max(0, Math.min(file.size, at - (ws?.bufferedAmount ?? 0)))
      let at = 0
      for (; at < file.size; at += CHUNK) {
        if (!this.pending.has(id)) break
        while (ws && ws.bufferedAmount > HIGH_WATER) { onProgress?.(sent()); await new Promise((r) => setTimeout(r, 15)) }
        this.sock.send(data.subarray(at, at + CHUNK))
        onProgress?.(sent())
      }
      await done
    }) as Promise<void>
  }

  /** The editor's save; resolves with the file's new mtime. `mtime` is what it had when opened (none: a new file). */
  save(path: string, text: string, mtime: number | undefined): Promise<number | undefined> {
    return this.serial(async () => {
      const id = crypto.randomUUID()
      const data = new TextEncoder().encode(text)
      const done = this.track(id, {})
      this.send({ type: 'save', id, path, bytes: data.byteLength, ...(mtime !== undefined ? { mtime_ms: mtime } : {}) })
      for (let at = 0; at < data.byteLength; at += CHUNK) {
        if (!this.pending.has(id)) break
        this.sock.send(data.slice(at, at + CHUNK))
      }
      return (await done) as number | undefined
    })
  }

  download(path: string): Promise<Blob> {
    return this.serial(() => this.request((id) => { this.downloading = id; return { type: 'download', id, path } })) as Promise<Blob>
  }

  private serial<T>(f: () => Promise<T>): Promise<T> {
    const run = this.queue.then(f, f)
    this.queue = run.catch(() => undefined)
    return run
  }

  private request(make: (id: string) => FilesClientMessage): Promise<Blob | number | void> {
    const id = crypto.randomUUID()
    const p = this.track(id, {})
    this.send(make(id))
    return p
  }

  private track(id: string, extra: Partial<Pending>): Promise<Blob | number | void> {
    return new Promise((resolve, reject) => this.pending.set(id, { resolve, reject, ...extra }))
  }

  private send(m: FilesClientMessage): void { this.sock.send(JSON.stringify(m)) }

  private failAll(why: string): void {
    for (const p of this.pending.values()) p.reject(new Error(why))
    this.pending.clear()
    this.downloading = null
  }

  private message(d: string | ArrayBuffer): void {
    if (typeof d !== 'string') {
      const p = this.downloading ? this.pending.get(this.downloading) : undefined
      if (!p || p.bytes === undefined) return
      p.parts!.push(d)
      p.got! += d.byteLength
      p.onProgress?.(p.got!)
      if (p.got! >= p.bytes) this.finishDownload()
      return
    }
    const m = JSON.parse(d) as FilesServerMessage
    switch (m.type) {
      case 'ready': this.on.ready(m); return
      case 'listing': this.on.listing(m.path, m.missing ? null : m.entries); return
      case 'done': this.pending.get(m.id)?.resolve(m.mtime_ms); this.pending.delete(m.id); return
      case 'failed': {
        this.pending.get(m.id)?.reject(new Error(m.message))
        this.pending.delete(m.id)
        if (this.downloading === m.id) this.downloading = null
        return
      }
      case 'download': {
        const p = this.pending.get(m.id)
        if (!p) return
        Object.assign(p, { bytes: m.bytes, parts: [], got: 0 })
        if (m.bytes === 0) this.finishDownload()
        return
      }
      case 'pong': return
      case 'git': this.on.git?.(m); return
    }
  }

  private finishDownload(): void {
    const id = this.downloading!
    const p = this.pending.get(id)!
    this.pending.delete(id)
    this.downloading = null
    p.resolve(new Blob(p.parts))
  }
}

/** `@path` with a trailing space, quoted when it has spaces, as Claude Code expects. */
export function mention(path: string): string {
  return /\s/.test(path) ? `@"${path}" ` : `@${path} `
}

export function joinPath(dir: string, name: string): string {
  return dir ? `${dir}/${name}` : name
}

/**
 * Text the file panel types into a session's terminal (a file reference);
 * the terminal of that session picks it up. Keyed by session so another
 * open session never receives it.
 */
export const useTermInsert = create<{ next: { sessionId: string; text: string; n: number } | null; insert: (sessionId: string, text: string) => void }>()((set, get) => ({
  next: null,
  insert: (sessionId, text) => set({ next: { sessionId, text, n: (get().next?.n ?? 0) + 1 } }),
}))

/** Drag type for a file dragged from the panel onto the terminal. */
export const DRAG_PATH = 'application/x-trisof-path'

/**
 * Files dropped on the terminal, like a local one: upload each into
 * `uploads/` on its own short-lived socket and return the paths to
 * reference. Works whether or not the file panel is open.
 */
export function uploadDropped(sessionId: string, files: File[], onProgress?: (text: string, fraction: number) => void, signal?: AbortSignal): Promise<string[]> {
  return new Promise((resolve, reject) => {
    let settled = false
    const fail = (e: Error) => { if (!settled) { settled = true; clearTimeout(timer); c.stop(); reject(e) } }
    const timer = setTimeout(() => fail(new Error('The server did not answer; is the session running?')), 20_000)
    // The chat's attachment tray removes a file mid-upload; stop rather than finish it.
    signal?.addEventListener('abort', () => fail(new DOMException('Cancelled', 'AbortError')), { once: true })
    const c: FilesClient = new FilesClient(sessionId, 'rw', {
      ready: (r) => {
        clearTimeout(timer)
        if (!r.writable) return fail(new Error('You can only look at this session'))
        const big = files.find((f) => f.size > r.max_upload)
        if (big) return fail(new Error(`${big.name} is over 100 MB`))
        void (async () => {
          const paths: string[] = []
          for (const f of await Promise.all(files.map(shrinkImage))) {
            const path = joinPath(r.uploads, f.name)
            await c.upload(path, f, (done) => {
              const k = done / Math.max(f.size, 1)
              onProgress?.(`Uploading ${f.name} · ${Math.round(k * 100)}%`, k)
            })
            paths.push(path)
          }
          if (settled) return
          settled = true
          c.stop()
          resolve(paths)
        })().catch(fail)
      },
      listing: () => {},
      state: (s) => { if (s === 'unreachable' || s === 'ended') fail(new Error('Cannot reach the server')) },
    })
  })
}

/** One file from the session folder on a short-lived socket: the chat's hover preview of a sent upload. */
export function downloadOnce(sessionId: string, path: string): Promise<Blob> {
  return new Promise((resolve, reject) => {
    let settled = false
    const end = (f: () => void) => { if (!settled) { settled = true; clearTimeout(timer); c.stop(); f() } }
    const timer = setTimeout(() => end(() => reject(new Error('The server did not answer'))), 20_000)
    const c: FilesClient = new FilesClient(sessionId, 'ro', {
      ready: () => { c.download(path).then((b) => end(() => resolve(b)), (e: Error) => end(() => reject(e))) },
      listing: () => {},
      state: (s) => { if (s === 'unreachable' || s === 'ended') end(() => reject(new Error('Cannot reach the server'))) },
    })
  })
}

/** Longest side Claude looks at; it scales anything bigger down itself. */
const IMAGE_EDGE = 2000

/**
 * A screenshot or photo dropped on the terminal, scaled down to what Claude
 * reads anyway, so a 6 MB Retina PNG uploads as a few hundred KB. Anything
 * else, or an image already small enough, goes as it is.
 */
export async function shrinkImage(f: File): Promise<File> {
  if (!/^image\/(png|jpeg|webp)$/.test(f.type) || f.size < 500 * 1024) return f
  try {
    const bmp = await createImageBitmap(f)
    const k = Math.min(1, IMAGE_EDGE / Math.max(bmp.width, bmp.height))
    const canvas = new OffscreenCanvas(Math.round(bmp.width * k), Math.round(bmp.height * k))
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    bmp.close()
    const type = f.type === 'image/png' ? 'image/png' : 'image/jpeg'
    const blob = await canvas.convertToBlob({ type, quality: 0.85 })
    return blob.size < f.size ? new File([blob], f.name, { type, lastModified: f.lastModified }) : f
  } catch { return f }
}
